import {
    Box,
    Dialog,
    DialogContent,
    IconButton,
    Tab,
    Tabs,
    Typography,
} from '@mui/material';
import {
    Image as ImageIcon,
    Layers3,
    Link2,
    LucideIcon,
    Sun,
    X,
} from 'lucide-react';
import { ReactNode, useId, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import DicomTagBrowser from '~/components/Dialogs/DicomTagBrowser';
import IconTile from '~/components/IconTile';
import SectionLabel from '~/components/SectionLabel';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import { DcmJsDatasetWrapper } from '~/domain/dicom/dcmJsWrapper';
import { ObjectDetailsContext } from './objectDetailsContext';

export type ObjectDetailsTab = 'details' | 'tags';

/** Whether an object's details dialog is open, and on which tab. */
export interface ObjectDetailsDialogState {
    tab: ObjectDetailsTab | null;
    open: (tab?: ObjectDetailsTab) => void;
    close: () => void;
}

/**
 * The open state of one object's details dialog, for a surface where more
 * than one control opens it: the (i) opens the details, a panel's "Show
 * DICOM" link opens the same dialog on its tags tab.
 */
export function useObjectDetailsDialog(): ObjectDetailsDialogState {
    const [tab, setTab] = useState<ObjectDetailsTab | null>(null);
    return useMemo(
        () => ({
            tab,
            open: (initialTab: ObjectDetailsTab = 'details') =>
                setTab(initialTab),
            close: () => setTab(null),
        }),
        [tab]
    );
}

/** What the dialog says about an object. */
export interface ObjectDetails {
    /** Message id naming the object type: `image`, `structureSet`, … */
    titleId: string;
    /** The object's own name. Treated as PHI. */
    name?: ReactNode;
    /**
     * One line of orienting detail under the name: modality, counts.
     * Rendered verbatim, so it must not carry PHI.
     */
    subtitle?: ReactNode;
    /** Status chips, approval state and similar, shown beside the close control. */
    badges?: ReactNode;
    /** The object's rename and delete controls, shown beside the close control. */
    actions?: ReactNode;
    /**
     * Parsed DICOM for this object. When present the dialog offers a
     * DICOM tags tab; when absent (a session image that has not been
     * downloaded) the tab is not shown rather than shown empty.
     */
    dataset?: DcmJsDatasetWrapper | null;
    /**
     * Details tab body, normally the object's existing `*Properties`
     * component. Mounted only while the dialog is open, so the data hooks
     * inside it do not run for every closed row in a list.
     */
    children: ReactNode;
}

/**
 * Icon per object type, keyed by the same message id that names it. Keeps
 * an image reading as an image whichever list it was opened from.
 */
const OBJECT_ICONS: Record<string, LucideIcon> = {
    image: ImageIcon,
    structureSet: Layers3,
    registration: Link2,
    dose: Sun,
};

/**
 * Both halves of "what is this object": the readable summary and the raw
 * DICOM behind it, as two tabs of one dialog rather than a link opening a
 * second. MUI `Dialog` carries the focus trap, focus restore and Escape
 * handling `docs/ui_specs.md` asks of a dialog.
 */
export default function ObjectDetailsDialog({
    dialog,
    ...details
}: ObjectDetails & { dialog: ObjectDetailsDialogState }) {
    /*
     * Mounted only while open, or a list of twenty rows would run twenty
     * sets of detail hooks for panels nobody opened. Remounting on each
     * open is also what makes the dialog start on the tab it was asked
     * for.
     */
    return dialog.tab === null ? null : (
        <OpenObjectDetailsDialog
            initialTab={dialog.tab}
            onClose={dialog.close}
            {...details}
        />
    );
}

function OpenObjectDetailsDialog({
    initialTab,
    onClose,
    ...details
}: ObjectDetails & {
    initialTab: ObjectDetailsTab;
    onClose: () => void;
}) {
    const { titleId, name, subtitle, badges, actions, dataset, children } =
        details;
    const intl = useIntl();
    // Normalised once, at mount: an ask for the tags of an object that has
    // none lands on the details, and stays there when a dataset arrives
    // later. Resolving it per render would throw a reader who is mid-panel
    // into the tag table the moment a streaming image finished loading.
    const [tab, setTab] = useState<ObjectDetailsTab>(
        initialTab === 'tags' && dataset ? 'tags' : 'details'
    );
    // The header is not a DialogTitle, so the dialog names itself from it:
    // the object type and the object's name, as a screen reader announces.
    const headingId = useId();

    const Icon = OBJECT_ICONS[titleId] ?? ImageIcon;
    const showTags = !!dataset;

    return (
        <Dialog
            open
            onClose={onClose}
            aria-labelledby={headingId}
            /*
             * React propagates through the portal to the row's own
             * handlers: a click would select the object underneath, a key
             * would drive the MenuList typeahead around some rows. Reading a
             * panel must not change what the user is working on.
             * Stopping a key also hides it from window listeners, which is
             * the same rule: no viewer shortcut acts behind the dialog.
             * Contour undo refuses under any open dialog by its own rule,
             * and Safe Mode listens in the capture phase, so it still works.
             */
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
            fullWidth
            /*
             * `sm` is narrower than the tag table's TABLE_MIN_WIDTH_PX, so a
             * dialog with the tags tab takes the wider size rather than
             * scrolling. It follows the dataset, not the tab, so switching
             * tabs never resizes the dialog under the pointer.
             */
            maxWidth={showTags ? 'md' : 'sm'}
            PaperProps={{
                elevation: 0,
                sx: { height: showTags ? '70vh' : undefined },
            }}
        >
            <Box
                px={2.5}
                pt={2}
                pb={0}
                display="flex"
                alignItems="center"
                gap={1.5}
            >
                <IconTile>
                    <Icon />
                </IconTile>
                <Box minWidth={0} flex={1}>
                    <Box id={headingId}>
                        <SectionLabel id={titleId} />
                        {name && (
                            <Typography fontSize={15} fontWeight={600} noWrap>
                                <SensitiveInfoTypography>
                                    {name}
                                </SensitiveInfoTypography>
                            </Typography>
                        )}
                    </Box>
                    {subtitle && (
                        <Typography fontSize={11} color="text.secondary" noWrap>
                            {subtitle}
                        </Typography>
                    )}
                </Box>
                <Box
                    display="flex"
                    alignItems="center"
                    gap={1}
                    flexShrink={0}
                    data-testid="object-details-badges"
                >
                    {badges}
                    {actions}
                    <IconButton
                        size="small"
                        onClick={onClose}
                        aria-label={intl.formatMessage({ id: 'close' })}
                        data-testid="object-details-close-button"
                        sx={{ p: 0.25 }}
                    >
                        <X size={16} />
                    </IconButton>
                </Box>
            </Box>

            {showTags && (
                <Box px={2.5} borderBottom={1} borderColor="divider">
                    <Tabs
                        value={tab}
                        onChange={(_, value) => setTab(value)}
                        sx={{ minHeight: 36 }}
                    >
                        <Tab
                            value="details"
                            label={intl.formatMessage({ id: 'details' })}
                            sx={{ minHeight: 36, fontSize: 12 }}
                        />
                        <Tab
                            value="tags"
                            label={intl.formatMessage({
                                id: 'dicom.tagViewer.title',
                            })}
                            sx={{ minHeight: 36, fontSize: 12 }}
                            data-testid="object-details-tags-tab"
                        />
                    </Tabs>
                </Box>
            )}

            <DialogContent
                sx={{
                    px: 2.5,
                    pt: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                }}
                data-testid="object-details-content"
            >
                {tab === 'details' ? (
                    <ObjectDetailsContext.Provider value>
                        {children}
                    </ObjectDetailsContext.Provider>
                ) : (
                    <DicomTagBrowser dataset={dataset ?? null} />
                )}
            </DialogContent>
        </Dialog>
    );
}
